/**
 * LocationInsuranceScreen
 *
 * Collects ZIP code, insurance carrier, and employer benefit.
 * Used for clinic matching and cost estimates.
 */

import React, { useState } from "react";
import { View, Text, Pressable, ScrollView, TextInput } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations, useRegion } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";

const EMPLOYER_BENEFITS = [
  "Carrot",
  "Progyny",
  "Maven",
  "Stork Club",
  "Kindbody",
  "Other",
];

type Props = {
  onNext: () => void;
};

export default function LocationInsuranceScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("LocationInsuranceScreen");
  const region = useRegion();
  const isFr = region !== "US";
  const { state, updateState } = usePlanBuilder();
  const [zip, setZip] = useState(state.zipCode ?? "");
  const [hasInsurance, setHasInsurance] = useState(state.hasInsurance);
  const [carrier, setCarrier] = useState(state.insuranceCarrier ?? "");
  const [hasBenefit, setHasBenefit] = useState(state.hasEmployerBenefit);
  const [benefit, setBenefit] = useState(state.employerBenefit);

  const handleZipChange = (text: string) => {
    const cleaned = text.replace(/\D/g, "").slice(0, 5);
    setZip(cleaned);
    updateState({ zipCode: cleaned || null });
  };

  const handleInsurance = (value: boolean) => {
    setHasInsurance(value);
    updateState({ hasInsurance: value });
  };

  const handleCarrier = (text: string) => {
    setCarrier(text);
    updateState({ insuranceCarrier: text || null });
  };

  const handleBenefit = (value: boolean | null) => {
    setHasBenefit(value);
    if (value !== true) {
      setBenefit(null);
      updateState({ hasEmployerBenefit: value, employerBenefit: null });
      return;
    }
    updateState({ hasEmployerBenefit: value });
  };

  const handleBenefitSelect = (name: string) => {
    setBenefit(name);
    updateState({ employerBenefit: name });
  };

  const canContinue = zip.length === 5;

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.location_insurance.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.location_insurance.subtitle")}
      </Text>

      {/* ZIP / Postal code */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          marginBottom: 8,
        }}
      >
        {isFr
          ? t("plan_builder.location_insurance.postal_label_fr")
          : t("plan_builder.location_insurance.zip_label")}
      </Text>
      <TextInput
        className="font-dm-sans"
        style={{
          borderWidth: 1,
          borderColor: EMBER.colors.border,
          borderRadius: 10,
          paddingHorizontal: 14,
          paddingVertical: 12,
          fontSize: 16,
          color: EMBER.colors.text,
          marginBottom: 24,
        }}
        keyboardType="number-pad"
        placeholder={
          isFr
            ? t("plan_builder.location_insurance.postal_placeholder_fr")
            : t("plan_builder.location_insurance.zip_placeholder")
        }
        placeholderTextColor={EMBER.colors.textFaint}
        value={zip}
        onChangeText={handleZipChange}
        maxLength={5}
      />

      {/* Insurance */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          marginBottom: 8,
        }}
      >
        {isFr
          ? t("plan_builder.location_insurance.insurance_label_fr")
          : t("plan_builder.location_insurance.insurance_label")}
      </Text>
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 16 }}>
        {[
          {
            value: true,
            label: t("plan_builder.location_insurance.insurance_yes"),
          },
          {
            value: false,
            label: t("plan_builder.location_insurance.insurance_no"),
          },
        ].map((opt) => {
          const isSelected = hasInsurance === opt.value;
          return (
            <Pressable
              key={String(opt.value)}
              onPress={() => {
                capturePress(
                  pressLabel(opt.label),
                  `location_insurance_has_insurance_${String(opt.value)}`,
                );
                handleInsurance(opt.value);
              }}
              style={{ flex: 1 }}
            >
              <View
                style={{
                  paddingVertical: 12,
                  borderRadius: 10,
                  backgroundColor: isSelected
                    ? `${EMBER.colors.text}12`
                    : `${EMBER.colors.textFaint}10`,
                  borderWidth: 1,
                  borderColor: isSelected
                    ? EMBER.colors.text
                    : EMBER.colors.border,
                  alignItems: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: isSelected ? "600" : "400",
                    color: isSelected
                      ? EMBER.colors.text
                      : EMBER.colors.textMid,
                  }}
                >
                  {opt.label}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>

      {/* Carrier */}
      {hasInsurance && (
        <View style={{ marginBottom: 24 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: EMBER.colors.textMid,
              marginBottom: 8,
            }}
          >
            {t("plan_builder.location_insurance.carrier_label")}
          </Text>
          <TextInput
            className="font-dm-sans"
            style={{
              borderWidth: 1,
              borderColor: EMBER.colors.border,
              borderRadius: 10,
              paddingHorizontal: 14,
              paddingVertical: 12,
              fontSize: 16,
              color: EMBER.colors.text,
            }}
            placeholder={
              isFr
                ? t("plan_builder.location_insurance.carrier_placeholder_fr")
                : t("plan_builder.location_insurance.carrier_placeholder")
            }
            placeholderTextColor={EMBER.colors.textFaint}
            value={carrier}
            onChangeText={handleCarrier}
          />
        </View>
      )}

      {/* Employer benefit — US only, hidden for FR */}
      {!isFr && (
        <>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: EMBER.colors.textMid,
              marginBottom: 8,
            }}
          >
            {t("plan_builder.location_insurance.benefit_label")}
          </Text>
          <View style={{ flexDirection: "row", gap: 8, marginBottom: 12 }}>
            {[
              {
                value: true,
                label: t("plan_builder.location_insurance.benefit_yes"),
              },
              {
                value: false,
                label: t("plan_builder.location_insurance.benefit_no"),
              },
              {
                value: null,
                label: t("plan_builder.location_insurance.benefit_not_sure"),
              },
            ].map((opt) => {
              const isSelected = hasBenefit === opt.value;
              return (
                <Pressable
                  key={String(opt.value)}
                  onPress={() => {
                    capturePress(
                      pressLabel(opt.label),
                      `location_insurance_benefit_${String(opt.value)}`,
                    );
                    handleBenefit(opt.value);
                  }}
                  style={{ flex: 1 }}
                >
                  <View
                    style={{
                      paddingVertical: 12,
                      borderRadius: 10,
                      backgroundColor: isSelected
                        ? `${EMBER.colors.text}12`
                        : `${EMBER.colors.textFaint}10`,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? EMBER.colors.text
                        : EMBER.colors.border,
                      alignItems: "center",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: isSelected ? "600" : "400",
                        color: isSelected
                          ? EMBER.colors.text
                          : EMBER.colors.textMid,
                      }}
                    >
                      {opt.label}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>

          {/* Benefit provider */}
          {hasBenefit && (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 16,
              }}
            >
              {EMPLOYER_BENEFITS.map((b) => {
                const isSelected = benefit === b;
                return (
                  <Pressable
                    key={b}
                    onPress={() => {
                      capturePress(pressLabel(b), "employer_benefit_option");
                      handleBenefitSelect(b);
                    }}
                  >
                    <View
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 7,
                        borderRadius: 8,
                        backgroundColor: isSelected
                          ? `${EMBER.colors.text}15`
                          : `${EMBER.colors.textFaint}12`,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? EMBER.colors.text
                          : "transparent",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          fontWeight: isSelected ? "600" : "400",
                          color: isSelected
                            ? EMBER.colors.text
                            : EMBER.colors.textMid,
                        }}
                      >
                        {b}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}
        </>
      )}

      {/* Continue */}
      <View style={{ marginTop: 16 }}>
        <WarmButton
          title={t("plan_builder.continue")}
          onPress={onNext}
          disabled={!canContinue}
        />
      </View>
    </ScrollView>
  );
}
